"use strict";

const FREE_SHIPPING_MARKETS = {
	CA: {
		currencyCode: "CAD",
		thresholdCents: 10000,
		thresholdLabel: "C$100",
	},
	US: {
		currencyCode: "USD",
		thresholdCents: 7200,
		thresholdLabel: "US$72",
	},
};
const ONE_OF_ONE_QUANTITY_MESSAGE = "One of a kind — only one is available.";

const mobileMenuToggle = document.querySelector("[data-mobile-menu-toggle]");
const mobileMenu = document.querySelector("[data-mobile-menu]");

const setMobileMenuOpen = (open) => {
	if (!mobileMenuToggle || !mobileMenu) return;
	mobileMenu.hidden = !open;
	mobileMenuToggle.setAttribute("aria-expanded", String(open));
	mobileMenuToggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
};

if (mobileMenuToggle && mobileMenu) {
	mobileMenuToggle.addEventListener("click", () => {
		setMobileMenuOpen(mobileMenu.hidden);
	});

	mobileMenu.addEventListener("click", (event) => {
		if (event.target.closest("a")) setMobileMenuOpen(false);
	});

	document.addEventListener("keydown", (event) => {
		if (event.key === "Escape" && !mobileMenu.hidden) {
			setMobileMenuOpen(false);
			mobileMenuToggle.focus();
		}
	});

	window.addEventListener("resize", () => {
		if (window.innerWidth > 900) setMobileMenuOpen(false);
	});
}

const getFreeShippingMarket = () => {
	const country = window.Shopify?.country || "CA";
	const activeCurrency = window.Shopify?.currency?.active || "CAD";
	const market = FREE_SHIPPING_MARKETS[country];
	return market?.currencyCode === activeCurrency ? market : null;
};

const qualifiesForFreeShipping = (priceCents, market) => {
	const directPriceCents = Number(priceCents);
	return (
		Number.isFinite(directPriceCents) &&
		directPriceCents > 0 &&
		directPriceCents + 0.5 >= market.thresholdCents
	);
};

const updateFreeShippingBadge = (badge, priceCents) => {
	if (!badge) return;
	badge.dataset.directPriceCents = String(priceCents || "");

	const market = getFreeShippingMarket();
	if (!market) {
		badge.textContent = "Shipping calculated at checkout";
		return;
	}

	badge.textContent = qualifiesForFreeShipping(priceCents, market)
		? "Free shipping"
		: `Free shipping on ${market.thresholdLabel}+ orders`;
};

const initFreeShippingBadges = (root) => {
	root.querySelectorAll("[data-free-shipping-badge]").forEach((badge) => {
		updateFreeShippingBadge(badge, badge.dataset.directPriceCents);
	});
};

const validateOneOfOneQuantity = (input) => {
	const isOneOfOne = input.dataset.oneOfOneQuantity === "true";
	const quantity = Number(input.value);
	input.setCustomValidity(
		isOneOfOne && Number.isFinite(quantity) && quantity > 1
			? ONE_OF_ONE_QUANTITY_MESSAGE
			: "",
	);
};

const initOneOfOneQuantityValidation = (root) => {
	root.querySelectorAll("[data-one-of-one-quantity]").forEach((input) => {
		if (input.dataset.oneOfOneValidationBound === "true") return;
		input.dataset.oneOfOneValidationBound = "true";
		input.addEventListener("input", () => validateOneOfOneQuantity(input));
		input.addEventListener("change", () => validateOneOfOneQuantity(input));
		input.addEventListener("invalid", () => validateOneOfOneQuantity(input));
		validateOneOfOneQuantity(input);
	});
};

const initProductRecommendations = (root) => {
	root.querySelectorAll("[data-product-discovery]").forEach(async (section) => {
		if (section.dataset.recommendationsLoading === "true") return;
		section.dataset.recommendationsLoading = "true";

		const results = section.querySelector("[data-product-recommendation-results]");
		const endpoint = section.dataset.recommendationsUrl;
		if (!results || !endpoint) return;

		const url = new URL(endpoint, window.location.origin);
		url.searchParams.set("section_id", section.dataset.sectionId);
		url.searchParams.set("product_id", section.dataset.productId);
		url.searchParams.set("limit", "4");
		url.searchParams.set("intent", "related");

		try {
			const response = await fetch(url, { headers: { Accept: "text/html" } });
			if (!response.ok) throw new Error("Product recommendations unavailable");
			const nextDocument = new DOMParser().parseFromString(
				await response.text(),
				"text/html",
			);
			const nextResults = nextDocument.querySelector(
				"[data-product-recommendation-results]",
			);
			if (nextResults?.childElementCount) {
				results.innerHTML = nextResults.innerHTML;
				initFreeShippingBadges(results);
			}
		} catch {
			// The full-collection link remains available when recommendations fail.
		}
	});
};

const syncShopifyLocalization = (nextDocument) => {
	const localizationSource = Array.from(nextDocument.scripts)
		.map((script) => script.textContent)
		.join("\n");
	const currencyMatch = localizationSource.match(
		/Shopify\.currency\s*=\s*(\{[^;]+\})\s*;/,
	);
	const countryMatch = localizationSource.match(
		/Shopify\.country\s*=\s*("[^"]+")\s*;/,
	);
	if (!currencyMatch || !countryMatch) return;

	try {
		window.Shopify = window.Shopify || {};
		window.Shopify.currency = JSON.parse(currencyMatch[1]);
		window.Shopify.country = JSON.parse(countryMatch[1]);
	} catch {
		// Keep the conservative threshold message when localization data is unavailable.
	}
};

const initProductSections = (root) => {
	root.querySelectorAll("[data-product-section]").forEach((section) => {
		const variantSelect = section.querySelector("[data-variant-select]");
		const request = section.querySelector("[data-sold-out-request]");
		const requestToggle = section.querySelector("[data-request-toggle]");
		const requestFields = section.querySelector("[data-request-fields]");

		requestToggle?.addEventListener("click", () => {
			const open = requestToggle.getAttribute("aria-expanded") === "true";
			requestToggle.setAttribute("aria-expanded", String(!open));
			requestFields.hidden = open;
			if (!open) {
				requestFields.querySelector("input:not([type='hidden']), textarea")?.focus();
			}
		});

		if (!variantSelect) return;

		const pricing = section.querySelector("[data-product-pricing]");
		const directPriceNote = section.querySelector("[data-direct-price-note]");
		const savings = section.querySelector("[data-product-savings]");
		const price = section.querySelector("[data-product-price]");
		const comparePrice = section.querySelector("[data-compare-price]");
		const shippingBadge = section.querySelector("[data-free-shipping-badge]");
		const availability = section.querySelector("[data-availability]");
		const addButton = section.querySelector("[data-add-to-cart]");
		const checkout = section.querySelector("[data-accelerated-checkout]");
		const quantity = section.querySelector("[data-quantity-input]");
		const purchaseControls = section.querySelector("[data-purchase-controls]");
		const requestVariant = section.querySelector("[data-request-variant]");
		const requestProductUrl = section.querySelector("[data-request-product-url]");

		variantSelect.addEventListener("change", () => {
			const option = variantSelect.selectedOptions[0];
			const available = option.dataset.available === "true";
			const oneOfOne = option.dataset.oneOfOne === "true";
			const hasMarketplacePrice = Boolean(option.dataset.comparePrice);

			price.textContent = option.dataset.price;
			updateFreeShippingBadge(shippingBadge, option.dataset.priceCents);
			comparePrice.textContent = option.dataset.comparePrice || "";
			pricing?.classList.toggle("is-marketplace-priced", hasMarketplacePrice);
			comparePrice.hidden = !hasMarketplacePrice;
			directPriceNote.hidden = !hasMarketplacePrice;
			savings.hidden = !hasMarketplacePrice;
			savings.textContent = hasMarketplacePrice
				? `(${option.dataset.savingsPercent}% off)`
				: "";
			availability.textContent = available
				? oneOfOne
					? "One of a kind · Ready to ship"
					: "Ready to ship"
				: "Sold out";
			availability.classList.toggle("unavailable", !available);
			addButton.disabled = !available;
			addButton.textContent = available ? "Add to cart" : "Sold out";
			checkout.classList.toggle("is-hidden", !available);
			purchaseControls.hidden = !available;
			request.hidden = available;
			if (available && requestToggle && requestFields) {
				requestToggle.setAttribute("aria-expanded", "false");
				requestFields.hidden = true;
			}

			const inventory = Number(option.dataset.inventoryQuantity);
			if (option.dataset.inventoryPolicy === "deny" && inventory > 0) {
				quantity.max = String(inventory);
				quantity.value = String(Math.min(Number(quantity.value), inventory));
			} else {
				quantity.removeAttribute("max");
			}
			quantity.dataset.oneOfOneQuantity = String(oneOfOne);
			validateOneOfOneQuantity(quantity);

			const media = option.dataset.mediaId
				? section.querySelector(`[data-media-id="${option.dataset.mediaId}"]`)
				: null;
			media?.scrollIntoView({ behavior: "smooth", block: "center" });

			const url = new URL(window.location.href);
			url.searchParams.set("variant", option.value);
			window.history.replaceState({}, "", url);
			if (requestVariant) requestVariant.value = option.dataset.variantTitle || "";
			if (requestProductUrl) requestProductUrl.value = url.toString();
		});
	});
};

const initDynamic = (root) => {
	initFreeShippingBadges(root);
	initOneOfOneQuantityValidation(root);
	initProductSections(root);
	initProductRecommendations(root);
};

// Swap only the localized markup so a currency change keeps the shopper on the same
// URL, query string and scroll position instead of reloading onto the home page.
const applyLocalization = async (form) => {
	await fetch(form.action, {
		method: "POST",
		body: new FormData(form),
		headers: { Accept: "text/html" },
	});

	const response = await fetch(window.location.href, {
		headers: { Accept: "text/html" },
		cache: "reload",
	});
	const nextDocument = new DOMParser().parseFromString(await response.text(), "text/html");
	syncShopifyLocalization(nextDocument);

	const currentMain = document.getElementById("MainContent");
	const nextMain = nextDocument.getElementById("MainContent");
	if (!currentMain || !nextMain) throw new Error("Localized content missing");

	currentMain.replaceWith(nextMain);
	document.querySelectorAll("[data-country-selector]").forEach((selector) => {
		const source = nextDocument.getElementById(selector.id);
		if (source) selector.value = source.value;
	});
	initDynamic(document);
};

document.querySelectorAll("[data-country-selector]").forEach((selector) => {
	selector.addEventListener("change", async () => {
		const form = selector.form;
		if (!form) return;

		selector.setAttribute("aria-busy", "true");
		try {
			await applyLocalization(form);
			setMobileMenuOpen(false);
		} catch (error) {
			form.submit();
		} finally {
			selector.removeAttribute("aria-busy");
		}
	});
});

initDynamic(document);